<template>
  <header v-if="visible" class="report-navigation">
    <nav class="report-navigation__inner" :aria-label="$t('reportNavigation.label')">
      <button
        type="button"
        class="report-navigation__back"
        :disabled="navigating"
        @click="goBack"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="m14.5 6-6 6 6 6" />
        </svg>
        <span>{{ $t('common.back') }}</span>
      </button>
      <div class="report-navigation__title" :title="pageTitle">{{ pageTitle }}</div>
      <button
        type="button"
        class="report-navigation__home"
        :aria-label="$t('reportNavigation.home')"
        :title="$t('reportNavigation.home')"
        :disabled="navigating"
        @click="goHome"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="m3.5 10 8.5-7 8.5 7M5.5 8.5V20h5v-6h3v6h5V8.5" />
        </svg>
      </button>
    </nav>
  </header>
</template>

<script>
import { REPORT_PAGE_TITLES, canGoBackInReport, getReportHome, shouldHideReportNavigation } from '@/utils/reportNavigation';

export default {
  name: 'ReportNavigation',
  data() {
    return {
      hideInCurrentEnvironment: true,
      navigating: false,
      releaseTimer: null
    };
  },
  computed: {
    visible() {
      return !this.hideInCurrentEnvironment && !!REPORT_PAGE_TITLES[this.$route.path] &&
        this.$route.path !== '/' && this.$route.path !== '/partner';
    },
    pageTitle() {
      const detailPaths = ['/details', '/nutrition', '/tissue', '/recuperate', '/condition'];
      const title = this.$route.query.title;
      if (detailPaths.includes(this.$route.path) && typeof title === 'string' && title.trim()) {
        return title;
      }
      return this.$t(REPORT_PAGE_TITLES[this.$route.path] || 'titles.home');
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        this.$emit('visibility-change', value);
      }
    },
    '$route.fullPath'() {
      this.updateNavigationVisibility();
      this.releaseNavigation();
    }
  },
  mounted() {
    this.detectEnvironment();
    window.addEventListener('flutterInAppWebViewPlatformReady', this.detectEnvironment);
  },
  beforeDestroy() {
    window.removeEventListener('flutterInAppWebViewPlatformReady', this.detectEnvironment);
    this.releaseNavigation();
  },
  methods: {
    updateNavigationVisibility() {
      this.hideInCurrentEnvironment = shouldHideReportNavigation({
        nativeApp: !!(window.flutter_inappwebview &&
          typeof window.flutter_inappwebview.callHandler === 'function'),
        route: this.$route,
        device: navigator
      });
    },
    detectEnvironment() {
      this.updateNavigationVisibility();
    },
    releaseNavigation() {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
      this.navigating = false;
    },
    navigate(homeOnly) {
      if (this.navigating) return;
      this.navigating = true;
      this.releaseTimer = setTimeout(this.releaseNavigation, 1500);
      if (!homeOnly && canGoBackInReport(this.$router)) {
        this.$router.back();
        return;
      }
      const home = getReportHome(this.$route, this.$store.getters.isThirdPartyMode);
      this.$router.replace(home).catch(error => {
        if (error.name !== 'NavigationDuplicated') {
          console.warn('[ReportNavigation] 返回报告失败', error);
          this.$showToast(this.$t('reportNavigation.failed'));
        }
        this.releaseNavigation();
      });
    },
    goBack() {
      this.navigate(false);
    },
    goHome() {
      this.navigate(true);
    }
  }
};
</script>

<style scoped lang="scss">
.report-navigation {
  position: sticky;
  top: 0;
  z-index: 20;
  padding-top: env(safe-area-inset-top, 0px);
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #e9edf5;
  box-shadow: 0 0.25rem 1.25rem rgba(24, 54, 100, 0.035);
  -webkit-backdrop-filter: blur(1rem);
  backdrop-filter: blur(1rem);
}

.report-navigation__inner {
  display: grid;
  grid-template-columns: 6.5rem minmax(0, 1fr) 6.5rem;
  align-items: center;
  gap: 0.75rem;
  max-width: 75rem;
  min-height: 4rem;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0.5rem 1.25rem;
  background: transparent;
}

.report-navigation__back,
.report-navigation__home {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.75;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 0.125rem solid #003cbf;
    outline-offset: 0.1875rem;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }
}

.report-navigation__back {
  justify-self: start;
  gap: 0.25rem;
  padding: 0.5rem 0.875rem 0.5rem 0.625rem;
  color: #003cbf;
  background: #f0f5ff;
  border: 1px solid #e1eaff;
  border-radius: 2rem;
}

.report-navigation__home {
  justify-self: end;
  width: 2.75rem;
  padding: 0.5rem;
  color: #63718b;
  background: #fff;
  border: 1px solid #e5eaf2;
  border-radius: 0.875rem;
}

.report-navigation__title {
  overflow: hidden;
  color: #24354d;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (hover: hover) {
  .report-navigation__back:hover:not(:disabled) {
    background: #e4edff;
    border-color: #c9d9ff;
  }

  .report-navigation__home:hover:not(:disabled) {
    color: #003cbf;
    background: #f5f8ff;
  }
}

@media (max-width: 480px) {
  .report-navigation__inner {
    grid-template-columns: 5.25rem minmax(0, 1fr) 5.25rem;
    gap: 0.375rem;
    min-height: 3.75rem;
    padding: 0.5rem 0.875rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .report-navigation__back,
  .report-navigation__home {
    transition: none;
  }
}

@media print {
  .report-navigation {
    display: none;
  }
}
</style>
